import { Button, Flex } from "@theorgchartdev/storybook";
import { buildUrlWithUTMParams, buildUrlWithUtmTerm } from "@/Utils/Misc";
import {
  gDemoReqSite,
  gPricingSite,
  gSuppressFeatureClickedParam,
  gSuppressLearnMoreLink,
  gWhiteLabel
} from "@coreapp/whitelabel/brand";
import { appstr } from "@coreapp/utils/appstr";
import { enumBrand, enumFeaturesList } from "@coreapp/constants";

type FeatureKey = (typeof enumFeaturesList)[keyof typeof enumFeaturesList];

const ISOLVED_CTA_TEXT: Partial<Record<FeatureKey, string>> = {
  [enumFeaturesList.BLS]: "Unlock advanced security",
  [enumFeaturesList.SCIM]: "Unlock",
  [enumFeaturesList.groupAutoAssignment]: "Unlock",
  [enumFeaturesList.autoExport]: "Unlock",
  [enumFeaturesList.auxMapping]: "Unlock",
  [enumFeaturesList.planApprovals]: "Unlock plan approvals",
  [enumFeaturesList.planning]: "Unlock planning",
  [enumFeaturesList.PIT]: "Unlock point in time",
  [enumFeaturesList.accessGroups]: "Unlock access groups",
  [enumFeaturesList.uiProfiles]: "Unlock UI profiles",
  [enumFeaturesList.adminLog]: "Unlock Admin Log"
};

const DIRECT_DEMO_UTM_TERMS: Partial<Record<FeatureKey, string>> = {
  [enumFeaturesList.BLS]: "upgrade-lp-book-a-demo-branch-level-security",
  [enumFeaturesList.planning]: "upgrade-lp-book-a-demo-planning",
  [enumFeaturesList.PIT]: "upgrade-lp-book-a-demo-point-in-time",
  [enumFeaturesList.groupAutoAssignment]: "upgrade-lp-book-a-demo-auto-group-assignment",
  [enumFeaturesList.SCIM]: "upgrade-lp-book-a-demo-scim",
  [enumFeaturesList.auxMapping]: "upgrade-lp-book-a-demo-aux-mapping",
  [enumFeaturesList.autoExport]: "upgrade-lp-book-a-demo-auto-export",
  [enumFeaturesList.adminLog]: "upgrade-lp-book-a-demo-admin-log"
};

interface UpgradeButtonsProps {
  featureKey?: FeatureKey;
  learnMoreUrl?: string;
}

const UpgradeButtons = ({ featureKey, learnMoreUrl }: UpgradeButtonsProps) => {
  const isIsolved = gWhiteLabel === enumBrand.isolved;
  const ctaText = appstr(
    (isIsolved && featureKey && ISOLVED_CTA_TEXT[featureKey]) || "Get started"
  );

  const openExternal = (url: Nullable<string>) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleGetStarted = () => {
    if (gSuppressFeatureClickedParam) {
      openExternal(gDemoReqSite);
      return;
    }
    const utmTerm = featureKey && DIRECT_DEMO_UTM_TERMS[featureKey];
    openExternal(
      utmTerm
        ? buildUrlWithUtmTerm(gDemoReqSite, utmTerm)
        : buildUrlWithUTMParams(gDemoReqSite, featureKey)
    );
  };

  const handleLearnMore = () => {
    if (gSuppressLearnMoreLink) return;

    // isolved-style brands suppress campaign attribution entirely - open the destination as-is.
    if (gSuppressFeatureClickedParam) {
      openExternal(learnMoreUrl || gPricingSite);
      return;
    }
    if (!learnMoreUrl) {
      openExternal(buildUrlWithUTMParams(gPricingSite, featureKey));
      return;
    }
    // The brand pricing site already carries the campaign UTM params; carry them onto the
    // feature-specific destination so both CTAs stay attributable to the same campaign.
    const utmParams = gPricingSite ? Object.fromEntries(new URL(gPricingSite).searchParams) : {};
    openExternal(buildUrlWithUTMParams(learnMoreUrl, featureKey, utmParams));
  };

  return (
    <Flex gap={"16px"} alignItems={"center"} direction={"row"}>
      <Button
        size={"medium"}
        modifier={"primary"}
        dataTestId={"upgrade-popover-get-started"}
        onClick={handleGetStarted}
      >
        {ctaText}
      </Button>
      <Button
        size={"medium"}
        modifier={"secondary"}
        dataTestId={"upgrade-popover-learn-more"}
        onClick={handleLearnMore}
      >
        {appstr("Learn more")}
      </Button>
    </Flex>
  );
};

export default UpgradeButtons;
